import { useState, useCallback, useRef } from 'react';

const useLazyOptions = (fetchFn) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const keyRef = useRef(null);

  const load = useCallback(async (key = null, force = false) => {
    const keyChanged = keyRef.current !== key;

    console.log("[useLazyOptions] load called", { key, force, keyChanged, prevKey: keyRef.current });

    // ✅ Clear options if key changes
    if (keyChanged) {
      console.log("[useLazyOptions] key changed → clearing old data");
      setData([]);
    }

    if (!force && (loading || (!keyChanged && data.length))) {
      console.log("[useLazyOptions] returning cached data", data);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchFn(key);
      console.log("[useLazyOptions] fetched result", result);
      setData(result || []);
      keyRef.current = key;
    } finally {
      setLoading(false);
    }
  }, [fetchFn, data.length, loading]);

  const reset = () => {
    console.log("[useLazyOptions] reset called → clearing all");
    keyRef.current = null;
    setData([]);
  };

  return { data, loading, load, reset };
};



const useLazyOptionsPerIndex = (fetchFn) => {
  const [dataMap, setDataMap] = useState({});
  const [loadingMap, setLoadingMap] = useState({});
  const keyMapRef = useRef({}); // key per index

  const load = async (key, index, force = false) => {
    const prevKey = keyMapRef.current[index];

    // ✅ Skip if same key already fetched unless forced
    if (!force && prevKey === key && dataMap[index] !== undefined) return;

    setLoadingMap(prev => ({ ...prev, [index]: true }));

    try {
      const result = await fetchFn(key);
      keyMapRef.current[index] = key;
      setDataMap(prev => ({
        ...prev,
        [index]: result || [],
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMap(prev => ({ ...prev, [index]: false }));
    }
  };

  const reset = () => {
    setDataMap({});
    setLoadingMap({});
    keyMapRef.current = {};
  };

  return {
    dataMap,
    loadingMap,
    load,
    reset,
  };
};
export { useLazyOptions, useLazyOptionsPerIndex };